import { ApiService, type ApiRequestConfig } from "./api.service";
import type { ExportStatus } from "@/components/data-table/use-csv-export";
import type {
  TCustomFieldArchivePreflight,
  TCustomFieldCreateInput,
  TCustomFieldDeletePreflight,
  TCustomFieldsListQuery,
  TCustomFieldUpdateInput,
  TCustomFieldUsage,
  TWorkspaceCustomField,
  TWorkspaceCustomFieldsPage,
} from "@/types/custom-field";

function buildListQuery(params: TCustomFieldsListQuery): string {
  const search = new URLSearchParams();
  if (params.cursor) search.set("cursor", params.cursor);
  if (params.per_page != null) search.set("per_page", String(params.per_page));
  if (params.q) search.set("q", params.q);
  if (params.sort) search.set("sort", params.sort);
  if (params.dir) search.set("dir", params.dir);
  if (params.include_archived) search.set("include_archived", "1");
  for (const k of params.kind ?? []) search.append("kind", k);
  // Repeated, not comma-joined: the backend reads these with `getlist`
  // (custom_fields/views.py → _cf_multi), same as `kind`.
  for (const c of params.category ?? []) search.append("category", c);
  for (const tid of params.template_id ?? []) search.append("template_id", tid);
  for (const fid of params.form_id ?? []) search.append("form_id", fid);
  const str = search.toString();
  return str ? `?${str}` : "";
}

export class WorkspaceCustomFieldService extends ApiService {
  async list(
    slug: string,
    params: TCustomFieldsListQuery = {},
    config?: ApiRequestConfig,
  ): Promise<TWorkspaceCustomFieldsPage> {
    return this.get(`/api/workspaces/${slug}/custom-fields/${buildListQuery(params)}`, config);
  }

  /**
   * Export the custom-fields list as CSV, scoped to the same filters/sort as
   * the list (`params`) and, when `body.ids` is set, restricted to those rows.
   * Returns the raw Response (200 stream / 202 async token).
   */
  async exportCustomFields(
    slug: string,
    body: { ids?: string[]; columns?: string[] },
    params: TCustomFieldsListQuery = {},
  ): Promise<Response> {
    return this.postRaw(
      `/api/workspaces/${slug}/export-custom-fields/${buildListQuery(params)}`,
      body,
    );
  }

  async getExportStatus(slug: string, token: string): Promise<ExportStatus> {
    return this.get(`/api/workspaces/${slug}/export-status/${token}/`);
  }

  async create(slug: string, data: TCustomFieldCreateInput): Promise<TWorkspaceCustomField> {
    return this.post(`/api/workspaces/${slug}/custom-fields/`, data);
  }

  async update(
    slug: string,
    fieldId: string,
    data: TCustomFieldUpdateInput,
  ): Promise<TWorkspaceCustomField> {
    return this.patch(`/api/workspaces/${slug}/custom-fields/${fieldId}/`, data);
  }

  async archive(slug: string, fieldId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/custom-fields/${fieldId}/`);
  }

  async restore(slug: string, fieldId: string): Promise<TWorkspaceCustomField> {
    return this.post(`/api/workspaces/${slug}/custom-fields/${fieldId}/restore/`, {});
  }

  async hardDelete(slug: string, fieldId: string): Promise<void> {
    return this.post(`/api/workspaces/${slug}/custom-fields/${fieldId}/delete/`, {});
  }

  async deletePreflight(slug: string, fieldId: string): Promise<TCustomFieldDeletePreflight> {
    return this.get(`/api/workspaces/${slug}/custom-fields/${fieldId}/delete-preflight/`);
  }

  async archivePreflight(slug: string, fieldId: string): Promise<TCustomFieldArchivePreflight> {
    return this.get(`/api/workspaces/${slug}/custom-fields/${fieldId}/archive-preflight/`);
  }

  async optionArchivePreflight(
    slug: string,
    fieldId: string,
    optionValue: string,
  ): Promise<TCustomFieldArchivePreflight> {
    return this.get(
      `/api/workspaces/${slug}/custom-fields/${fieldId}/option-archive-preflight/?option=${encodeURIComponent(optionValue)}`,
    );
  }

  async duplicate(slug: string, fieldId: string): Promise<TWorkspaceCustomField> {
    return this.post(`/api/workspaces/${slug}/custom-fields/${fieldId}/duplicate/`, {});
  }

  async usage(
    slug: string,
    fieldId: string,
    config?: ApiRequestConfig,
  ): Promise<TCustomFieldUsage> {
    return this.get(`/api/workspaces/${slug}/custom-fields/${fieldId}/usage/`, config);
  }
}
